import React, { useEffect, useState, useRef } from "react";
import { connect } from "react-redux";
import { RichText } from "@learningblocks/shared-components/lib/RichText";
import {
  updateSpreadsheetOverwrites,
  setPlotRequired,
} from "../Spreadsheet/Reducers/actions";
import {
  initObjectives,
  completeStep,
  incrementStep,
  completeProtocol,
  completeAndSkipStep,
  incrementCurrentStepSheetChanges,
  incrementUnsanitisedSheetChanges,
} from "./Reducers/actions";
import IconStart from "@learningblocks/protocol-constants/lib/Icons/start-solid";
import IconHexagon from "@learningblocks/protocol-constants/lib/Icons/hexagon-solid";
import className from "./style.module.scss";
import { StepAssist } from "./StepAssist";

const ObjectivesContainer = (props) => {
  const objectiveContainer = useRef(null);

  const {
    protocol,
    objectives,
    stepState = {},
    spreadsheet = {},
    sceneIndex,
    initObjectives,
    currentObjectiveIndex,
    currentStepGroupIndex,
    currentStepIndex,
    currentStep,
  } = props;

  const [selectedObjectiveIndex, setSelectedObjectiveIndex] = useState(
    currentObjectiveIndex || 0
  );

  // Initialize objectives in Redux state if a scene with the given ID does not already exist
  useEffect(() => {
    const sceneExists = stepState.scenes?.some(
      (scene) => scene.id === sceneIndex
    );

    if (!sceneExists) {
      initObjectives([
        {
          id: sceneIndex,
          objectives: props.stepData.objectives,
        },
      ]);
    }
  }, [protocol, sceneIndex, initObjectives]);

  // On mount, apply step overrides and scroll to top
  useEffect(() => {
    if (currentStepIndex !== undefined) {
      applyStepOverrides();
      scrollStepToTop();
    }
  }, [currentStepIndex, currentStepGroupIndex, selectedObjectiveIndex]);

  // Monitor sheetChanges and trigger increment action when it changes
  useEffect(() => {
    if (Object.prototype.hasOwnProperty.call(currentStep, "assistance")) {
      let selection = "";
      spreadsheet.data.map((sheet) => {
        if (sheet.id === currentStep.assistance.watchCells.sheetId) {
          selection = sheet.selection;
        }
      });

      // Check if any cell in the array falls within the specified range (true by default if array is empty)
      const isAnyCellInRange =
        currentStep.assistance.watchCells.cellRange.length === 0 ||
        currentStep.assistance.watchCells.cellRange.some((cell) =>
          isCellInRange(cell, selection)
        );

      // If at least one cell is within the range, perform the action
      if (isAnyCellInRange) {
        // Dispatch the increment action when sheetChanges changes
        props.incrementCurrentStepSheetChanges({
          sceneId: sceneIndex,
          objectiveIndex: currentObjectiveIndex,
          stepGroupIndex: currentStepGroupIndex,
          stepIndex: currentStepIndex,
        });
      }
      // always increment unsanitised sheet changes for step for cases when assist checks want more interaction data
      props.incrementUnsanitisedSheetChanges({
        sceneId: sceneIndex,
        objectiveIndex: currentObjectiveIndex,
        stepGroupIndex: currentStepGroupIndex,
        stepIndex: currentStepIndex,
      });
    }
  }, [spreadsheet.changeCount]); // Re-run this effect whenever changeCount changes

  const isCellInRange = (cell, range) => {
    // Check if cell is defined and is a string
    if (!cell || typeof cell !== "string") {
      return false;
    }

    // Handle undefined or null range (treat as no match)
    if (!range || typeof range !== "string") {
      return false;
    }

    // Function to extract the column (e.g., "A") and row (e.g., 1) from a cell reference
    const parseCell = (cell) => {
      const match = cell.match(/^([A-Z]+)(\d+)$/);
      if (!match) {
        return null; // Return null if the cell format is incorrect
      }
      return { column: match[1], row: parseInt(match[2], 10) };
    };

    // Parse the provided cell
    const parsedCell = parseCell(cell);
    if (!parsedCell) {
      return false; // Invalid cell format
    }

    // If the range is a single cell (e.g., "A2")
    if (!range.includes(":")) {
      const parsedRangeCell = parseCell(range);
      if (!parsedRangeCell) {
        return false; // Invalid range cell format
      }
      // Return true if the cell matches the single-cell range
      return (
        parsedCell.column === parsedRangeCell.column &&
        parsedCell.row === parsedRangeCell.row
      );
    }

    // If the range is a multi-cell range (e.g., "A2:D5")
    const [startCell, endCell] = range.split(":");
    const parsedStart = parseCell(startCell);
    const parsedEnd = parseCell(endCell);

    // Check if any of the parsed results are null (invalid format)
    if (!parsedStart || !parsedEnd) {
      return false;
    }

    // Check if the cell is in the range (same column and within the row range)
    if (
      parsedCell.column >= parsedStart.column &&
      parsedCell.column <= parsedEnd.column
    ) {
      return (
        parsedCell.row >= parsedStart.row && parsedCell.row <= parsedEnd.row
      );
    }

    // If the cell is not in the range, return false
    return false;
  };

  const scrollStepToTop = () => {
    const element = document.querySelector(".spreadSheetSim_currentStep");
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  // Update selected objective index when the current index changes
  useEffect(() => {
    setSelectedObjectiveIndex(currentObjectiveIndex || 0);
  }, [currentObjectiveIndex]);

  // Check if the current step is complete
  useEffect(() => {
    isCurrentStepComplete();
  }, [protocol]);

  // Scroll to the top of the objective list when switching objectives
  useEffect(() => {
    if (objectiveContainer.current) {
      objectiveContainer.current.scrollTop = 0;
      scrollStepToTop();
    }
  }, [selectedObjectiveIndex]);

  // Apply the step overrides from the spreadsheet
  const applyStepOverrides = () => {
    // Create a mutable copy of queued overwrites
    const updatedQueuedOverwrites = [...(spreadsheet.queuedOverwrites || [])];

    currentStep.overwrites?.forEach((key) => {
      if (Object.prototype.hasOwnProperty.call(spreadsheet.overwrites, key)) {
        updatedQueuedOverwrites.push(spreadsheet.overwrites[key]);
      }
    });

    props.updateSpreadsheetOverwrites(updatedQueuedOverwrites);
  };

  // Check if the current step is complete and increment if necessary
  const isCurrentStepComplete = () => {
    if (currentStep.complete && !protocol.complete) {
      let needToIncrement = true;

      const isEndOfGroup =
        currentStepIndex ===
        objectives?.[currentObjectiveIndex]?.stepGroups?.[currentStepGroupIndex]
          ?.steps?.length -
          1;
      if (isEndOfGroup) {
        const isGroupEndOfObjective =
          currentStepGroupIndex ===
          objectives?.[currentObjectiveIndex]?.stepGroups?.length - 1;
        if (isGroupEndOfObjective) {
          const isObjectiveEndOfProtocol =
            currentObjectiveIndex === objectives?.length - 1;
          if (isObjectiveEndOfProtocol) {
            needToIncrement = false;
            props.completeProtocol({ sceneId: sceneIndex });
            props.goToSection(props.nextSection);
          }
        }
      }

      if (needToIncrement) {
        props.incrementStep({
          sceneId: sceneIndex,
          objectiveIndex: currentObjectiveIndex,
          stepGroupIndex: currentStepGroupIndex,
          stepIndex: currentStepIndex,
        });
      }
    }
  };

  // Check if the given step ID matches the current step ID
  const isCurrentStep = (stepId) => {
    return currentStep.id === stepId;
  };

  // Check if a step is in a future group or objective
  const isFutureStep = (stepGroupIndex) => {
    const isFutureObjective = selectedObjectiveIndex > currentObjectiveIndex;
    const isCurrentObjective = selectedObjectiveIndex === currentObjectiveIndex;
    const isFutureGroup = stepGroupIndex > currentStepGroupIndex;

    return isFutureObjective || (isCurrentObjective && isFutureGroup);
  };

  // Complete a given step
  const completeStep = (stepGroupIndex, stepIndex) => {
    props.completeStep({
      sceneId: sceneIndex,
      objectiveIndex: currentObjectiveIndex,
      stepGroupIndex: stepGroupIndex,
      stepIndex: stepIndex,
    });
  };

  // Complete and skip a step
  const completeAndSkipStep = (stepGroupIndex, stepIndex) => {
    props.completeAndSkipStep({
      sceneId: sceneIndex,
      objectiveIndex: currentObjectiveIndex,
      stepGroupIndex: stepGroupIndex,
      stepIndex: stepIndex,
    });
  };

  // Check if the current step meets the completion criteria
  useEffect(() => {
    checkStepCompletionCriteria();
  }, [spreadsheet, currentStep]);

  /**
   * Checks whether the step passes its criteria.
   */
  const checkStepCompletionCriteria = () => {
    if (isPassing(currentStep) && !currentStep.manuallyConfirm) {
      if (
        currentStep ===
        objectives[currentObjectiveIndex].stepGroups[currentStepGroupIndex]
          .steps[currentStepIndex]
      ) {
        completeStep(currentStepGroupIndex, currentStepIndex);
      }
    } else if (
      !objectives?.[currentObjectiveIndex]?.stepGroups?.[currentStepGroupIndex]
        ?.required
    ) {
      let futureGroupPassed = false;
      let futureGroupRequiredAndNotPassed = false;

      for (
        let i = currentStepGroupIndex + 1;
        i < objectives?.[currentObjectiveIndex]?.stepGroups?.length;
        i++
      ) {
        let groupPassed = false;

        objectives[currentObjectiveIndex].stepGroups[i].steps.forEach(
          (step) => {
            if (
              step.complete ||
              (isPassing(step) && !step.requiresManualConfirm)
            ) {
              groupPassed = true;
            }
          }
        );

        if (objectives[currentObjectiveIndex].stepGroups[i].required) {
          if (!groupPassed) {
            futureGroupRequiredAndNotPassed = true;
          } else {
            futureGroupPassed = true;
            completeStep(i, 0);
          }
          break;
        }
      }

      if (!futureGroupRequiredAndNotPassed && futureGroupPassed) {
        completeAndSkipStep(currentStepGroupIndex, currentStepIndex);
      }
    } else {
      objectives[currentObjectiveIndex].stepGroups[
        currentStepGroupIndex
      ].steps.map((scene, index) => {
        if (isPassing(scene) && !scene.manuallyConfirm) {
          completeStep(currentStepGroupIndex, index);
        }
      });
    }
  };

  /**
   * Checks if the current step meets its pass criteria.
   */
  const isPassing = (step) => {
    if (!Array.isArray(step.passCriteria)) {
      return false; // Return false or handle as per your logic
    }

    if (step.passCriteria.length === 0) {
      return true;
    }

    let passing = 0;

    step.passCriteria.map((check) => {
      if (check.type === "CELL_VALUE") {
        const value = getCellValue(
          check.sheetId,
          check.row,
          check.col,
          check.valueType
        );

        switch (check.match) {
          case "NOT_EMPTY":
            if (value !== "" && value !== undefined) {
              passing++;
            }
            break;
          case "IS_EMPTY":
            if (value === "" || value === undefined) {
              passing++;
            }
            break;
          case "MATCH_CELL":
            if (
              value ===
              getCellValue(
                check.matchCell.sheetId,
                check.matchCell.row,
                check.matchCell.col,
                check.matchCell.valueType
              )
            ) {
              passing++;
            }
            break;
          default:
            if (value == check.match) {
              passing++;
            }
            break;
        }
      } else if (check.type === "PLOT_DATA") {
        const value = getPlotValue(
          check.sheetId,
          check.plotId,
          check.valueType
        );
        switch (check.match) {
          case "NOT_EMPTY":
            if (value != null && value !== "") {
              passing++;
            }
            break;
          case "IS_EMPTY":
            if (!value) {
              passing++;
            }
            break;
          case "NOT_DEFAULT":
            if (
              (check.valueType == "height" && value !== 300) ||
              (check.valueType == "width" && value !== 400)
            ) {
              passing++;
            }
            break;
          default:
            if (value == check.match) {
              passing++;
              props.setPlotRequired(check.plotId);
            }
            break;
        }
      } else if (check.type === "SELECTION") {
        spreadsheet.data.map((sheet) => {
          if (sheet.id === check.sheetId) {
            if (sheet.selection == check.match) {
              passing++;
            }
          }
        });
      } else if (check.type === "!SELECTION") {
        spreadsheet.data.map((sheet) => {
          if (sheet.id === check.sheetId) {
            if (sheet.selection != check.match) {
              passing++;
            }
          }
        });
      }
    });

    // Pass if all criteria are passed or if only one criteria needs to passed and has (if negativePass, then invert to negatively align the criteria)
    let pass =
      (step.onlyOnePassNeeded && passing > 0) ||
      passing === step.passCriteria.length;
    if (step.negativePass) {
      pass = !pass;
    }
    return pass;
  };

  /**
   * Retrieve the value of a cell.
   */
  const getCellValue = (sheetId, row, col, valueType) => {
    let value;

    spreadsheet.data.forEach((sheet) => {
      // Ensure the sheet ID matches and the row/col data exists
      if (
        sheet.id === sheetId &&
        sheet.data &&
        sheet.data[row] &&
        sheet.data[row][col]
      ) {
        const cell = sheet.data[row][col];

        switch (valueType) {
          case "live":
            if (Object.prototype.hasOwnProperty.call(cell, "live")) {
              value = cell.live;
            } else if (Object.prototype.hasOwnProperty.call(cell, "m")) {
              value = cell.m;
            } else {
              value = cell.v;
            }
            break;
          case "value":
            if (Object.prototype.hasOwnProperty.call(cell, "v")) {
              value = cell.v;
            }
            break;
          case "visual":
            if (Object.prototype.hasOwnProperty.call(cell, "m")) {
              value = cell.m;
            }
            break;
          case "format":
            if (
              Object.prototype.hasOwnProperty.call(cell, "ct") &&
              Object.prototype.hasOwnProperty.call(cell.ct, "fa")
            ) {
              value = cell.ct.fa;
            }
            break;
          case "formula":
            if (Object.prototype.hasOwnProperty.call(cell, "f")) {
              value = cell.f;
            }
            break;
          default:
            break;
        }
      }
    });

    return value;
  };

  /**
   * Retrieve the value of a plot.
   */
  const getPlotValue = (sheetId, plotId, valueType) => {
    let value;
    Object.entries(spreadsheet.plots).forEach(([, plot]) => {
      if (plot.id === plotId && sheetId === plot.sheet) {
        switch (valueType) {
          case "id":
            value = plot.id;
            break;
          case "range":
            value = plot.cellsUsed.range;
            break;
          case "title":
            value = plot.title;
            break;
          case "headerX":
            value = plot.headerX;
            break;
          case "headerY":
            value = plot.headerY;
            break;
          case "height":
            value = plot.size.y;
            break;
          case "width":
            value = plot.size.x;
            break;
          case "bin_width":
            value = plot?.histogramBinWidth;
            break;
          case "range_min":
            value = plot?.histogramRangeMin;
            break;
          case "range_max":
            value = plot?.histogramRangeMax;
            break;
          default:
            break;
        }
      }
    });

    return value;
  };

  /**
   * Generate a step character (letter) based on the step index.
   */
  const getStepCharacter = (
    stepGroups,
    currentGroupIndex,
    currentStepIndex
  ) => {
    let stepIndex = 0;
    for (let i = 0; i < currentGroupIndex; i++) {
      if (stepGroups[i]) {
        stepIndex = stepIndex + stepGroups[i].steps.length;
      }
    }
    stepIndex = stepIndex + currentStepIndex + 1;

    if (stepIndex > 26) {
      // account for double letters for higher step index (above the alphabet fromCharCode)
      return `${String.fromCharCode(96 + stepIndex - 26)}${String.fromCharCode(96 + stepIndex - 26)}`;
    } else {
      return String.fromCharCode(96 + stepIndex);
    }
  };

  if (!stepState.scenes?.some((scene) => scene.id === sceneIndex)) {
    return <div>Loading...</div>;
  }

  if (!objectives) {
    return <div>No objectives available for this scene.</div>;
  }

  return (
    <div className={className.objectivesContainer}>
      <div className={className.progressContainer}>
        <div className={className.progressBar}>
          {objectives.map((objective, index) => (
            <button
              key={index}
              tabIndex={0}
              className={`${className.objectiveProgressItem} ${selectedObjectiveIndex >= index ? className.active : ""}`}
              onClick={() => {
                setSelectedObjectiveIndex(index);
              }}
              aria-label={`Go to objective ${index + 1}.`}
            ></button>
          ))}
        </div>
        <span className={className.progressStep}>
          {selectedObjectiveIndex + 1}/{objectives.length}
        </span>
      </div>
      <div className={className.objectiveTitle}>
        {selectedObjectiveIndex + 1}.{" "}
        <RichText text={objectives[selectedObjectiveIndex]?.title} />
      </div>

      <div className={className.objectivesList} ref={objectiveContainer}>
        <ul className={className.protocolTasksContainer}>
          {objectives[selectedObjectiveIndex]?.stepGroups?.map(
            (stepGroup, stepGroupIndex) =>
              stepGroup.steps?.map((step, stepIndex) => {
                return (
                  <li
                    key={step.id}
                    className={`${className.protocolTask} ${isCurrentStep(step.id) ? className.currentTask : ""} ${step.complete ? className.completedTask : ""} ${isCurrentStep(step.id) ? "spreadSheetSim_currentStep" : ""} ${!stepGroup.required ? className.optional : ""}`}
                    style={{ scrollMarginTop: "38px" }}
                  >
                    <div
                      className={`${className.protocolTaskWrapper} ${step.complete && !step.skipped ? className.noSkipped : ""}`}
                    >
                      <div className={className.index}>
                        <IconHexagon
                          style={{
                            color: isCurrentStep(step.id)
                              ? "white"
                              : step.complete
                                ? "#668ba6"
                                : "#003e6b",
                            height: "29px",
                          }}
                        />
                        <span
                          style={{
                            color: isCurrentStep(step.id) ? "#003e6b" : "white",
                          }}
                        >
                          {step.complete && !step.skipped
                            ? " "
                            : getStepCharacter(
                                objectives[selectedObjectiveIndex].stepGroups,
                                stepGroupIndex,
                                stepIndex
                              )}
                        </span>
                      </div>

                      <span className={className.taskText}>
                        {!stepGroup.required && (
                          <div className={className.optionalTxt}>
                            {step.skipped ? (
                              <RichText text={"Skipped"} />
                            ) : (
                              <RichText text={"Optional"} />
                            )}
                          </div>
                        )}
                        <div>
                          {typeof step.title === "string" && (
                            <RichText addTagClasses={false} text={step.title} />
                          )}
                          {typeof step.title === "object" && (
                            <RichText
                              addTagClasses={false}
                              {...JSON.parse(JSON.stringify(step.title))}
                            />
                          )}
                        </div>
                      </span>
                    </div>
                    {Object.prototype.hasOwnProperty.call(step, "assistance")
                      ? step.assistance.levels.map((assist, key) => (
                          <StepAssist
                            key={key}
                            assist={assist}
                            step={step}
                            isCurrentStep={isCurrentStep(step.id)}
                            isPassing={isPassing}
                          />
                        ))
                      : null}
                    {step.manuallyConfirm && (
                      <button
                        tabIndex={0}
                        className={className.continueBtn}
                        disabled={
                          !isPassing(step) ||
                          isFutureStep(stepGroupIndex) ||
                          step.complete
                        }
                        onClick={() => {
                          completeStep(stepGroupIndex, stepIndex);
                        }}
                      >
                        Continue
                      </button>
                    )}
                  </li>
                );
              })
          )}
        </ul>

        <div
          className={`${className.objectiveFooter} ${selectedObjectiveIndex === 0 ? className.noPrev : ""}`}
        >
          {selectedObjectiveIndex != 0 && (
            <button
              tabIndex={0}
              aria-label="Previous button. Select to display the previous objective. "
              onClick={() =>
                setSelectedObjectiveIndex(selectedObjectiveIndex - 1)
              }
            >
              <IconStart
                style={{
                  color: "#003e6b",
                  height: "16px",
                  top: "2px",
                  transform: "rotate(180deg)",
                }}
              />{" "}
              Previous
            </button>
          )}
          <div
            role="button"
            tabIndex={0}
            aria-label="Go to current step."
            onKeyDown={() => {
              setSelectedObjectiveIndex(currentObjectiveIndex);
            }}
            className={className.indicator}
            onClick={() => {
              setSelectedObjectiveIndex(currentObjectiveIndex);
              scrollStepToTop();
            }}
          >
            <span>
              {currentObjectiveIndex +
                1 +
                "." +
                getStepCharacter(
                  objectives[selectedObjectiveIndex].stepGroups,
                  currentStepGroupIndex,
                  currentStepIndex
                )}
            </span>
            <IconHexagon
              style={{
                color: "#003e6b",
                height: "46px",
              }}
            />
          </div>
          <button
            tabIndex={0}
            aria-label="Next button. Select to display the next objective."
            onClick={() => {
              if (selectedObjectiveIndex === objectives.length - 1) {
                props.goToSection(props.nextSection);
              } else {
                setSelectedObjectiveIndex(selectedObjectiveIndex + 1);
              }
            }}
          >
            <IconStart
              style={{ color: "#003e6b", height: "16px", top: "2px" }}
            />
            {selectedObjectiveIndex === objectives.length - 1
              ? "Continue"
              : "Next"}
          </button>
        </div>
      </div>
    </div>
  );
};

function mapStateToProps(state, ownProps) {
  const sceneIndex = 8;
  const stepState = state.stepData || {};
  const protocol =
    stepState.scenes?.find((scene) => scene.id === sceneIndex) ||
    ownProps.stepData;
  const currentObjectiveIndex = protocol?.currentObjectiveIndex || 0;
  const currentStepGroupIndex = protocol?.currentStepGroupIndex || 0;
  const currentStepIndex = protocol?.currentStepIndex || 0;

  return {
    ...ownProps,
    protocol: protocol,
    objectives: protocol.objectives,
    stepState: stepState,
    spreadsheet: state.spreadsheet.spreadsheetData,
    sceneIndex: sceneIndex,
    currentObjectiveIndex: currentObjectiveIndex,
    currentStepGroupIndex: currentStepGroupIndex,
    currentStepIndex: currentStepIndex,
    currentStep:
      protocol.objectives?.[currentObjectiveIndex]?.stepGroups?.[
        currentStepGroupIndex
      ]?.steps?.[currentStepIndex] || {},
  };
}

function mapDispatchToProps(dispatch) {
  return {
    updateSpreadsheetOverwrites: (data) =>
      dispatch(updateSpreadsheetOverwrites(data)),
    completeStep: (data) => dispatch(completeStep(data)),
    incrementStep: (data) => dispatch(incrementStep(data)),
    completeProtocol: (data) => dispatch(completeProtocol(data)),
    completeAndSkipStep: (data) => dispatch(completeAndSkipStep(data)),
    incrementCurrentStepSheetChanges: (data) =>
      dispatch(incrementCurrentStepSheetChanges(data)),
    incrementUnsanitisedSheetChanges: (data) =>
      dispatch(incrementUnsanitisedSheetChanges(data)),
    setPlotRequired: (data) => dispatch(setPlotRequired(data)),
    initObjectives: (data) => dispatch(initObjectives(data)),
  };
}

const MappedObjectivesContainer = connect(
  mapStateToProps,
  mapDispatchToProps
)(ObjectivesContainer);

export { MappedObjectivesContainer as ObjectivesContainer };
